import React, { useEffect, useState } from "react";
import {
  Flex,
  SimpleGrid,
  Box,
  Text,
  Button,
  Icon,
  Spinner,
  useColorModeValue,
  Badge,
  HStack,
  VStack,
  Divider,
} from "@chakra-ui/react";
import {
  FiBarChart2,
  FiUsers,
  FiHome,
  FiMessageCircle,
  FiMap,
  FiInfo,
  FiMapPin,
  FiArrowRight,
  FiActivity,
  FiShield,
  FiLayers,
} from "react-icons/fi";
import { getToken } from "utils/auth";
import axios from "axios";
import config from "config";
import { FaChartPie, FaDna } from "react-icons/fa";
import { Link } from "react-router-dom";

function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cardBg = useColorModeValue("white", "navy.700");
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const textColor = useColorModeValue("slate.800", "white");
  const subtitleColor = useColorModeValue("slate.500", "slate.400");
  const heroBg = useColorModeValue(
    "linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #0284c7 100%)",
    "linear-gradient(135deg, #0b1329 0%, #111c38 50%, #0d9488 100%)"
  );

  useEffect(() => {
    const fetchIndividual = async () => {
      try {
        setLoading(true);
        const token = getToken();
        const response = await axios.get(
          `${config.API_URL}/highlights`, {
          headers: { Authorization: `Bearer ${token}` },
        }
        );
        setData(response.data);
      } catch (err) {
        setError("Failed to fetch dashboard highlights data");
      } finally {
        setLoading(false);
      }
    };

    fetchIndividual();
  }, []);

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="teal.500" thickness="4px" />
          <Text color={subtitleColor} fontSize="sm">Loading scientific database highlights...</Text>
        </VStack>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Box bg={cardBg} p={8} borderRadius="20px" border="1px solid" borderColor="red.300" textAlign="center">
          <Text color="red.500" fontSize="lg" fontWeight="bold">{error}</Text>
          <Button mt={4} colorScheme="teal" size="sm" onClick={() => window.location.reload()}>
            Retry Loading
          </Button>
        </Box>
      </Flex>
    );
  }

  const date = data?.latest_post?.date ? new Date(data.latest_post.date) : null;
  const readableDate = date ? date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }) : "No recent activity";

  return (
    <Flex direction="column" pt={{ base: "62px", md: "75px", xl: "88px" }} px={{ base: "0px", md: "0px" }} gap="28px">
      {/* Hero Banner Section */}
      <Box
        bg={heroBg}
        borderRadius="24px"
        p={{ base: "4", sm: "6", md: "8" }}
        color="white"
        position="relative"
        overflow="hidden"
        shadow="0 15px 35px rgba(13, 148, 136, 0.25)"
      >
        <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "start", md: "center" }} gap="20px">
          <VStack align="start" spacing="12px" maxW="700px">
            <Text fontSize={{ base: "xl", sm: "2xl", md: "3xl" }} fontWeight="800" letterSpacing="-0.02em" lineHeight="1.2">
              Tunisian Xeroderma Pigmentosum Database
            </Text>
            <Text fontSize={{ base: "xs", sm: "sm", md: "md" }} opacity={0.9} fontWeight="400">
              National database tracking XP patient cohorts, genealogical pedigrees, geographic distribution, and genetic mutations in Tunisia.
            </Text>
          </VStack>

          <HStack spacing="12px" wrap="wrap">
            <Box bg="whiteAlpha.200" backdropFilter="blur(10px)" p={{ base: "10px 16px", sm: "14px 20px" }} borderRadius="16px" textAlign="center" minW={{ base: "110px", sm: "130px" }}>
              <Text fontSize={{ base: "xl", sm: "2xl" }} fontWeight="800" color="cyan.300">{data?.total_individuals || "—"}</Text>
              <Text fontSize="xs" textTransform="uppercase" opacity={0.85} fontWeight="600">Individuals</Text>
            </Box>
            <Box bg="whiteAlpha.200" backdropFilter="blur(10px)" p={{ base: "10px 16px", sm: "14px 20px" }} borderRadius="16px" textAlign="center" minW={{ base: "110px", sm: "130px" }}>
              <Text fontSize={{ base: "xl", sm: "2xl" }} fontWeight="800" color="cyan.300">{data?.total_families || "—"}</Text>
              <Text fontSize="xs" textTransform="uppercase" opacity={0.85} fontWeight="600">Families</Text>
            </Box>
          </HStack>
        </Flex>
      </Box>

      {/* Main Grid Cards */}
      {data && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="24px">
          {/* Individuals Section */}
          <Box
            bg={cardBg}
            borderRadius="20px"
            p={{ base: "16px", sm: "20px", md: "24px" }}
            border="1px solid"
            borderColor={cardBorder}
            shadow="sm"
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-6px)",
              shadow: "xl",
              borderColor: "teal.400",
            }}
            display="flex"
            flexDirection="column"
          >
            <Flex justify="space-between" align="center" mb="16px">
              <Flex justify="center" align="center" w="48px" h="48px" borderRadius="14px" bg="teal.50" _dark={{ bg: "teal.900" }}>
                <Icon as={FiUsers} boxSize="24px" color="teal.500" />
              </Flex>
              <Badge colorScheme="teal" borderRadius="full" px="10px">Active Database</Badge>
            </Flex>

            <Text fontSize="xl" fontWeight="700" color={textColor} mb="4px">
              Individuals Cohort
            </Text>
            <Text fontSize="sm" color={subtitleColor} mb="20px">
              Comprehensive clinical & genetic profiles of studied XP individuals.
            </Text>

            <Box mt="auto" mb="20px" p="14px" bg="slate.50" _dark={{ bg: "navy.800" }} borderRadius="14px">
              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={subtitleColor}>Total Cataloged:</Text>
                <Text fontSize="xs" fontWeight="700" color={textColor}>{data?.total_individuals || "N/A"}</Text>
              </Flex>
            </Box>

            <Button
              colorScheme="teal"
              variant="solid"
              w="full"
              as={Link}
              to="/txd/individuals"
              rightIcon={<FiArrowRight />}
              borderRadius="12px"
            >
              Explore Individuals
            </Button>
          </Box>

          {/* Families Section */}
          <Box
            bg={cardBg}
            borderRadius="20px"
            p={{ base: "16px", sm: "20px", md: "24px" }}
            border="1px solid"
            borderColor={cardBorder}
            shadow="sm"
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-6px)",
              shadow: "xl",
              borderColor: "teal.400",
            }}
            display="flex"
            flexDirection="column"
          >
            <Flex justify="space-between" align="center" mb="16px">
              <Flex justify="center" align="center" w="48px" h="48px" borderRadius="14px" bg="cyan.50" _dark={{ bg: "cyan.900" }}>
                <Icon as={FiHome} boxSize="24px" color="cyan.500" />
              </Flex>
              <Badge colorScheme="cyan" borderRadius="full" px="10px">Genealogies</Badge>
            </Flex>

            <Text fontSize="xl" fontWeight="700" color={textColor} mb="4px">
              Family Tree Catalog
            </Text>
            <Text fontSize="sm" color={subtitleColor} mb="20px">
              Consanguinity patterns & kinships grouped by familial lineages.
            </Text>

            <Box mt="auto" mb="20px" p="14px" bg="slate.50" _dark={{ bg: "navy.800" }} borderRadius="14px">
              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={subtitleColor}>Total Families:</Text>
                <Text fontSize="xs" fontWeight="700" color={textColor}>{data?.total_families || "N/A"}</Text>
              </Flex>
            </Box>

            <Button
              colorScheme="teal"
              variant="solid"
              w="full"
              as={Link}
              to="/txd/families"
              rightIcon={<FiArrowRight />}
              borderRadius="12px"
            >
              Browse Families
            </Button>
          </Box>

          {/* Statistics Section */}
          <Box
            bg={cardBg}
            borderRadius="20px"
            p={{ base: "16px", sm: "20px", md: "24px" }}
            border="1px solid"
            borderColor={cardBorder}
            shadow="sm"
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-6px)",
              shadow: "xl",
              borderColor: "teal.400",
            }}
            display="flex"
            flexDirection="column"
          >
            <Flex justify="space-between" align="center" mb="16px">
              <Flex justify="center" align="center" w="48px" h="48px" borderRadius="14px" bg="purple.50" _dark={{ bg: "purple.900" }}>
                <Icon as={FiBarChart2} boxSize="24px" color="purple.500" />
              </Flex>
              <Badge colorScheme="purple" borderRadius="full" px="10px">Analytics</Badge>
            </Flex>

            <Text fontSize="xl" fontWeight="700" color={textColor} mb="4px">
              Genomic Analytics
            </Text>
            <Text fontSize="sm" color={subtitleColor} mb="20px">
              Graphical insights on age distribution, mutations, and consanguinity.
            </Text>

            <Flex justify="center" align="center" py="10px" my="auto">
              <Icon as={FaChartPie} boxSize="48px" color="purple.400" opacity={0.8} />
            </Flex>

            <Button
              colorScheme="teal"
              variant="solid"
              w="full"
              as={Link}
              to="/txd/stats"
              rightIcon={<FiArrowRight />}
              borderRadius="12px"
              mt="20px"
            >
              View Analytics
            </Button>
          </Box>

          {/* Map Section */}
          <Box
            bg={cardBg}
            borderRadius="20px"
            p={{ base: "16px", sm: "20px", md: "24px" }}
            border="1px solid"
            borderColor={cardBorder}
            shadow="sm"
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-6px)",
              shadow: "xl",
              borderColor: "teal.400",
            }}
            display="flex"
            flexDirection="column"
          >
            <Flex justify="space-between" align="center" mb="16px">
              <Flex justify="center" align="center" w="48px" h="48px" borderRadius="14px" bg="blue.50" _dark={{ bg: "blue.900" }}>
                <Icon as={FiMapPin} boxSize="24px" color="blue.500" />
              </Flex>
              <Badge colorScheme="blue" borderRadius="full" px="10px">GIS Mapping</Badge>
            </Flex>

            <Text fontSize="xl" fontWeight="700" color={textColor} mb="4px">
              Geographic Map
            </Text>
            <Text fontSize="sm" color={subtitleColor} mb="20px">
              Regional density and governorate distribution across Tunisia.
            </Text>

            <Flex justify="center" align="center" py="10px" my="auto">
              <Icon as={FiMap} boxSize="48px" color="blue.400" opacity={0.8} />
            </Flex>

            <Button
              colorScheme="teal"
              variant="solid"
              w="full"
              as={Link}
              to="/txd/mapdata"
              rightIcon={<FiArrowRight />}
              borderRadius="12px"
              mt="20px"
            >
              Open Interactive Map
            </Button>
          </Box>

          {/* Forum Section */}
          <Box
            bg={cardBg}
            borderRadius="20px"
            p={{ base: "16px", sm: "20px", md: "24px" }}
            border="1px solid"
            borderColor={cardBorder}
            shadow="sm"
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-6px)",
              shadow: "xl",
              borderColor: "teal.400",
            }}
            display="flex"
            flexDirection="column"
          >
            <Flex justify="space-between" align="center" mb="16px">
              <Flex justify="center" align="center" w="48px" h="48px" borderRadius="14px" bg="orange.50" _dark={{ bg: "orange.900" }}>
                <Icon as={FiMessageCircle} boxSize="24px" color="orange.500" />
              </Flex>
              <Badge colorScheme="orange" borderRadius="full" px="10px">Discussions</Badge>
            </Flex>

            <Text fontSize="xl" fontWeight="700" color={textColor} mb="4px">
              Forum
            </Text>
            <Text fontSize="sm" color={subtitleColor} mb="16px">
              Research discussions & scientific announcements.
            </Text>

            <Box p="12px" bg="slate.50" _dark={{ bg: "navy.800" }} borderRadius="14px" mb="20px" mt="auto">
              <Text fontSize="xs" fontWeight="700" color={textColor} noOfLines={1}>
                {data?.latest_post?.title || "No recent posts available"}
              </Text>
              <Text fontSize="xs" color={subtitleColor} mt="4px">
                {readableDate}
              </Text>
            </Box>

            <Button
              colorScheme="teal"
              variant="solid"
              w="full"
              as={Link}
              to="/txd/forums"
              rightIcon={<FiArrowRight />}
              borderRadius="12px"
            >
              Go to Forums
            </Button>
          </Box>


        </SimpleGrid>
      )}
    </Flex>
  );
}

export default Dashboard;

